/* import __COLOCATED_TEMPLATE__ from './profile-overlay-company.hbs'; */
import { all } from 'rsvp';
import { bool } from '@ember/object/computed';
import { action } from '@ember/object';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { dropTask, task } from 'ember-concurrency';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import {
  COMPANY_PROFILE__COMPANY_INCLUDES,
  COMPANY_PROFILE__COMPANY_INCLUDES_ADMIN,
  COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES,
  COMPANY_PROFILE__POSTINGS_INCLUDES,
  COMPANY_PROFILE__POSTINGS_TO_LOAD_COUNT,
} from 'sh-www/constants/routes/company-profile';
import { COMPANY_PROFILE_COMPONENT } from 'sh-www/components/company-profile/company-profile';
import {
  ADMIN_CREDIT_DEFAULT_INCLUDES,
  ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES,
  PAGINATION_DEFAULT_COUNT_PER_PAGE,
} from 'sh-www/constants/routes/reports';
import { COMPANY_CREDIT } from 'sh-www/models/company-credit';

const CLASS_NAME = 'SH-c-profile-overlay-company';

export default Component.extend({
  tagName: '',
  classNames: [CLASS_NAME],
  CLASS_NAME,

  /** Register services */
  applicationStateManager: service('state-managers/application'),
  assignmentCommandService: service('commands/assignment-commands'),
  companyCommandService: service('commands/company-commands'),
  companyContactService: service('commands/company-contact-commands'),
  companyTeamCommandService: service('commands/company-team-commands'),
  costCodeCommandsService: service('commands/cost-code-commands'),
  userCommandService: service('commands.user-commands'),
  errorTracker: service('error-tracker'),
  noteCommandService: service('commands/note-commands'),
  session: service(),
  shData: service(),
  shGeneralInvoiceCommandService: service('commands/sh-general-invoice-commands'),
  store: service(),


  /** @param {Company|null} */
  company: null,

  /** @param {User|null} */
  user: null,

  /** @param {Function} */
  onSetProfileRef: () => {},

  /** @type {Ember.A<Assignment>} */
  postings: A([]),

  /** @type {String} */
  section: '',

  /** @type {String|null} The ID of the company currently loaded. */
  _loadedCompanyID: null,

  /** @type {Boolean} Whether any profile is loading. */
  isAnyProfileLoading: bool('manageProfile.isRunning'),

  /** @inheritDoc */
  didReceiveAttrs() {
    this._super(...arguments);

    if (this.get('_loadedCompanyID') !== this.get('company.id')) {
      this.get('manageProfile').perform();
    }
  },

  /**
   * Loads the company.
   *
   * @param {String} companySlug
   * @return {Ember.RSVP.Promise}
   */
  loadCompany(companySlug) {
    const { currentUser } = this.session;
    const baseIncludes = currentUser.isAdmin
      ? COMPANY_PROFILE__COMPANY_INCLUDES_ADMIN
      : COMPANY_PROFILE__COMPANY_INCLUDES;

    const companyIncludes = Object.assign([], baseIncludes);

    if (currentUser.superAdmin) {
      companyIncludes.push('subscription');
    }

    return this.companyCommandService
      .loadCompany(companySlug, companyIncludes);
  },

  reloadCompanyTask: task(function *() {
    const company = yield this.loadCompany(this.company.id);
    this.set('company', company);
  }).drop(),

  reloadCompanyTeamsTask: task(function *(companyId) {
    try {
      const response = yield this.companyTeamCommandService.loadCompanyTeams(companyId);
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'companyTeam');
    } catch (e) {
      this.errorTracker.error('Failed to reload company teams');
      this.applicationStateManager.addToast('Failed to reload teams');
    }
  }).drop(),

  onSaveCompanyTeamTask: task(function *(companyId, name, description, colorHex, companyTeamId = null) {
    try {
      if (companyTeamId) {
        return yield this.companyTeamCommandService.updateCompanyTeam({
          companyTeamId,
          name,
          description,
          colorHex,
        });
      }

      return yield this.companyTeamCommandService.storeCompanyTeam({
        companyId,
        name,
        description,
        colorHex,
      });
    } catch (e) {
      this.errorTracker.error('Failed to save company team');
      this.applicationStateManager.addToast('Failed to save team');
    }
  }).drop(),

  onDeleteCompanyTeamTask: task(function *(companyTeamId) {
    try {
      return yield this.companyTeamCommandService.destroyCompanyTeam(companyTeamId);
    } catch (e) {
      this.errorTracker.error('Failed to delete company team');
      this.applicationStateManager.addToast('Failed to delete team');
    }
  }).drop(),

  loadCompanyCreditFactoidsTask: task(function *() {
    try {
      return yield this.store.query('factoid', {
        type: COMPANY_CREDIT.FACTOID_TYPE,
        filter: {
          company_id: this.company.id,
        },
      });
    } catch (e) {
      this.errorTracker.error('Failed to load company credit factoids');
      this.applicationStateManager.addToast('Failed to load factoids');
    }
  }).drop(),

  loadCompanyCostCodeCreditsTask: task(function *() {
    const { company } = this;
    const costCodeIncludes = company.canUseCompanyTeams
        ? ['company-teams']
        : [];

    try {
      const costCodes = yield this.costCodeCommandsService.getCostCodesForCompany(
        company,
        costCodeIncludes,
      );

      return {
        costCodes,
        costCodesCurrentPage: 1,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load company credits');
      this.applicationStateManager.addToast('Failed to load credits');
    }
  }).drop(),

  loadCreditPaidInvoicesTask: task(function *(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES,
        filters: {
          company_id: this.company.id,
          type: 'CompanyCredit',
          paid: true,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralPaidInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralPaidInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralPaidInvoices,
        shGeneralPaidInvoicesPagination,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load paid credit invoices');
      this.applicationStateManager.addToast('Failed to load paid credit invoices');
    }
  }).drop(),

  loadCreditUnpaidInvoicesTask: task(function *(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES,
        filters: {
          company_id: this.company.id,
          type: 'CompanyCredit',
          paid: false,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralUnpaidInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralUnpaidInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralUnpaidInvoices,
        shGeneralUnpaidInvoicesPagination,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load unpaid credit invoices');
      this.applicationStateManager.addToast('Failed to load unpaid credit invoices');
    }
  }).drop(),

  loadCreditDeletedInvoicesTask: task(function *(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES,
        filters: {
          company_id: this.company.id,
          type: 'CompanyCredit',
          only_trashed: currentUser.isAdmin,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralDeletedInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralDeletedInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralDeletedInvoices,
        shGeneralDeletedInvoicesPagination,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load deleted credit invoices');
      this.applicationStateManager.addToast('Failed to load deleted credit invoices');
    }
  }).drop(),

  /**
   * Loads the notes made on a company.
   *
   * @param {Company} company
   * @return {Ember.RSVP.Promise}
   */
  loadCompanyNotes: task(function *(company) {
    try {
      return yield this.noteCommandService.loadAdminNotesOnCompany(
        company,
        'created-by-user',
      );
    } catch (e) {
      this.errorTracker.error(`loadCompanyNotes failed for company id ${company.id}`, this, e);
      this.applicationStateManager.addToast('Failed to load company notes');
    }
  }).drop(),

  /**
   * Loads the members of the company and invitees.
   *
   * @param {Number} companyId
   * @return {Ember.RSVP.Promise}
   */
  loadCompanyMembersTask: task( function *(companyId, includeTeams = false) {
    const { currentUser } = this.session;
    const isCompanyAdminOrHigher = currentUser.isAdmin || currentUser.isCompanyAdmin;

    const companyMemberIncludes = isCompanyAdminOrHigher
      ? ['all-employees.company-membership', 'invites']
      : ['approved-employees.company-membership'];

    if (includeTeams) {
      companyMemberIncludes.push('company-teams');

      if (isCompanyAdminOrHigher) {
        companyMemberIncludes.push(`all-employees.${COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES}`);
      } else {
        companyMemberIncludes.push(`approved-employees.${COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES}`);
      }
    }

    return yield this.companyCommandService.loadCompany(companyId, companyMemberIncludes);
  }).drop(),

  /**
   * Loads the a company's billing info.
   *
   * @param {Company} company
   * @return {Ember.RSVP.Promise}
   */
  loadCompanyBilling: task(function *(company) {
     try {
       return yield this.companyCommandService.loadCompany(
        company.id,
         [
           'company-billing',
         ],
       );
     } catch (e) {
       this.errorTracker.error(`loadCompanyBilling failed for company id ${company.id}`, this, e);
       this.applicationStateManager.addToast('Failed to load company billing');
     }
   }).drop(),

  /**
   * Loads the a company's billing contacts.
   *
   * @param {Company} company
   * @return {Ember.RSVP.Promise}
   */
  loadCompanyContacts: task(function *(company) {
    try {
      const response = yield this.companyContactService.loadCompanyContacts(
        company.id,
        true,
        ['cost-codes:withTrashed'],
      );
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'companyContact');
    } catch (e) {
      this.errorTracker.error(`loadCompanyContacts failed for company id ${company.id}`, this, e);
      this.applicationStateManager.addToast('Failed to load company billing contacts');
    }
  }).drop(),

  /**
   * Loads default fees for sellers.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadDefaultFees: task(function *() {
     try {
       return yield this.companyCommandService.defaultFees();
     } catch (e) {
       this.errorTracker.error('loadDefaultFees failed', this, e);
       this.applicationStateManager.addToast('Failed to load default fees');
     }
   }).drop(),

  loadUser: action(function(user, company) {
    return this.userCommandService.getUser(user.id, [`company-seller-connections:company_id(${company.id})`]);
  }),

  /**
   * Loads the postings.
   *
   * @param {String} companySlug
   * @return {Ember.RSVP.Promise}
   */
  loadPostings(companySlug) {
    const postingIncludes = [...COMPANY_PROFILE__POSTINGS_INCLUDES];

    if (this.session.currentUser.isSeller) {
      postingIncludes.push('applications');
      postingIncludes.push('assignment-user-meta');
    }

    return this.assignmentCommandService.loadAssignmentsDefault(
      ASSIGNMENT.STATUS.LIVE,
      1,
      COMPANY_PROFILE__POSTINGS_TO_LOAD_COUNT,
      undefined,
      postingIncludes,
      [],
      { company: companySlug, user_company_teams: this.session.currentUser.id },
    );
  },

  reloadPostingsTask: task(function *() {
    const postings = yield this.loadPostings(this.company.slug);
    this.set('postings', postings);
  }).drop(),

  onUpdateNote: task(function *(note, newNoteContent) {
    const objectIsSavingKey = `isSavingEditedNoteMap.${note.id}`;

    this.set(objectIsSavingKey, true);

    try {
      yield this.noteCommandService.updateNote(
        note,
        newNoteContent
      );

      this.set('isEditingNoteMap', {
        [note.id]: false,
      });
    } catch (e) {
      this.errorTracker.error(`onUpdateNote failed note id ${note.id}`, this, e);
      this.applicationStateManager.addToast('Failed to update note');
    } finally {
      this.set(objectIsSavingKey, false);
    }
  }).drop(),

  onDeleteNote: task(function *(note) {
    try {
      return yield this.noteCommandService.deleteNote(note);
    } catch (e) {
      this.errorTracker.error(`onDeleteNote failed note id ${note.id}`, this, e);
      this.applicationStateManager.addToast('Failed to delete note');
    }
  }).drop(),

  onDeleteCompanyContact: task(function *(companyContact) {
    const { id } = companyContact;
    try {
      return this.companyContactService.destroyCompanyContact(
        id,
      );
    } catch (e) {
      this.errorTracker.error(`onDeleteCompanyContact failed for company contact id ${id}`, this, e);
      this.applicationStateManager.addToast('Failed to delete company contact');
    }
  }).drop(),

  onUpdateCompanyContact: task(function *(companyContact, newEmail) {
    const { id } = companyContact;
    try {
      return yield this.companyContactService.updateCompanyContact({
        companyContactId: id,
        email: newEmail,
      });
    } catch (e) {
      throw e;
    }
  }).drop(),

  onUpdateCompanyBillingSettings: task(function *(company, params) {
    try {
      const response = yield this.companyCommandService.updateBillingSettings(company, params);
      this.applicationStateManager.addToast('Update successful');
      return response;
    } catch (e) {
      throw e;
    }
  }).drop(),

  saveAdminCompanyNote: task(function *(company, noteContent) {
    try {
      yield this.noteCommandService.saveAdminNoteOnCompany(
        company,
        noteContent,
      );
      this.applicationStateManager.addToast('Saved note');
    } catch (e) {
      this.errorTracker.error('Failed too save note on company');
      this.applicationStateManager.addToast('Failed to save note');
      if (e.errors?.content) {
        this.applicationStateManager.addToast(e.errors.content);
      }
    }
  }).drop(),

  /** Loads the incoming company. */
  manageProfile: task(function * () {
    try {
      if (this.session.currentUser.isSeller) {
        const [company, user] = yield all([
          this.loadCompany(this.company.id),
          this.loadUser(this.session.currentUser, this.company)
        ]);

        this.setProperties({
          user,
          company,
          _loadedCompanyID: company.id
        });
      } else {
        const [company, postings] = yield all([
          this.loadCompany(this.company.id),
          this.loadPostings(this.company.id),
        ]);

        this.setProperties({
          postings,
          section: COMPANY_PROFILE_COMPONENT.SECTIONS.PROFILE,
          _loadedCompanyID: company.id
        });
      }
    } catch (error) {
      const message = `manageProfile -- can not load company ${this.company.id}`;
      this.errorTracker.error(message, this, error);
      throw error;
    }
  }),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    this.setProperties({
      isSavingEditedNoteMap: {},
      isEditingNoteMap: {},
    });
  },

  actions: {
    onToggleEditExistingNote(noteId, isEditing) {
      this.set('isEditingNoteMap', {
        [noteId]: isEditing,
      });
    },

    /**
     * Called when the user navigates between tabs.
     *
     * @param {String} section
     */
    changeSection(section) {
      this.set('section', section);
    },
  },
});
